import React from 'react';

import { NumericFormat } from 'react-number-format';
import {
  MenuItem,
  TextField,
  Box,
  Popover,
  Stack,
  IconButton,
  InputAdornment,
  Tooltip,
  Collapse,
  Typography,
} from '@mui/material';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

/*
 * Version 1.0
 */

interface IProps {
  label: string;
  type:
    | 'password'
    | 'string'
    | 'email'
    | 'number'
    | 'boolean'
    | 'select'
    | 'date'
    | 'euro'
    | 'color';

  attr: string;
  object: any;
  setObject: Function;
  required?: boolean;
  variant?: 'filled' | 'outlined' | 'standard';
  rows?: number;
  children?: React.ReactElement[];
  size?: 'small' | 'medium';
  null?: boolean;
  disabled?: boolean;
  onChangeHandler?: Function;
  onFocus?: Function;
  selectNoValueLabel?: string;
  maxWidth?: number | string;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  maxLength?: number | 'sm' | 'md' | 'lg';
  error?: boolean;
  helperText?: React.ReactNode;
}

export const CustomeTextField: React.FC<IProps> = props => {
  const getValue = () => {
    if (
      props.null !== undefined &&
      props.null &&
      props.object[props.attr] === null
    ) {
      return '';
    } else {
      return props.object[props.attr];
    }
  };

  const resolveMaxLength = (): number | undefined => {
    if (props.maxLength === undefined) return undefined;

    if (typeof props.maxLength === 'number') {
      return props.maxLength;
    }

    switch (props.maxLength) {
      case 'sm':
        return 45;
      case 'md':
        return 100;
      case 'lg':
        return 254;
      default:
        return undefined;
    }
  };

  const resolvedMaxLength = resolveMaxLength();

  const getStringValue = (): string => {
    const value = getValue();
    if (value === null || value === undefined) return '';
    return String(value);
  };

  const currentLength = getStringValue().length;

  const hasMaxLength =
    resolvedMaxLength !== undefined &&
    (props.type === 'string' ||
      props.type === 'email' ||
      props.type === 'password' ||
      props.type === 'date' ||
      props.type === 'color');

  const isTooLong =
    hasMaxLength &&
    resolvedMaxLength !== undefined &&
    currentLength > resolvedMaxLength;

  const helperText = hasMaxLength
    ? `${currentLength}/${resolvedMaxLength}${
        isTooLong ? ` – Maximal ${resolvedMaxLength} Zeichen erlaubt` : ''
      }`
    : undefined;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (props.onChangeHandler !== undefined) {
      props.onChangeHandler(event);
    } else {
      const changeObject = { ...props.object };

      if (props.type === 'boolean') {
        changeObject[props.attr] = event.target.value === 'true';
      } else if (
        props.type === 'password' ||
        props.type === 'string' ||
        props.type === 'email' ||
        props.type === 'date' ||
        props.type === 'color'
      ) {
        if (
          props.null !== undefined &&
          props.null &&
          event.target.value === ''
        ) {
          changeObject[props.attr] = null;
        } else {
          changeObject[props.attr] = event.target.value;
        }
      } else if (props.type === 'number' || props.type === 'select') {
        if (
          props.null !== undefined &&
          props.null &&
          (event.target.value === '' || Number(event.target.value) === -255)
        ) {
          changeObject[props.attr] = null;
        } else {
          changeObject[props.attr] = Number(event.target.value);
        }
      }

      props.setObject(changeObject);
    }
  };

  const handleChangeNumber = (values: any, sourceInfo: any) => {
    const changeObject = { ...props.object };
    changeObject[props.attr] =
      values.floatValue === undefined ? 0 : values.floatValue;
    props.setObject(changeObject);
  };

  if (props.type === 'select') {
    return (
      <>
        <TextField
          variant={props.variant === undefined ? 'outlined' : props.variant}
          label={props.label}
          fullWidth
          sx={{ maxWidth: props.maxWidth ?? '100%' }}
          name={`${props.type}-${props.attr}`}
          value={
            props.null && props.object[props.attr] === null
              ? -255
              : Number(props.object[props.attr])
          }
          onChange={handleChange}
          required={props.required == undefined ? false : props.required}
          select
          size={props.size !== undefined ? props.size : 'small'}
          disabled={props.disabled === undefined ? false : props.disabled}
          onBlur={props.onBlur ? props.onBlur : undefined}
        >
          {props.null && (
            <MenuItem key='empty-null' value={-255}>
              <i>
                {props.selectNoValueLabel === undefined
                  ? `Kein Wert`
                  : props.selectNoValueLabel}
              </i>
            </MenuItem>
          )}
          {props.children}
        </TextField>
      </>
    );
  } else if (props.type === 'boolean') {
    return (
      <>
        <TextField
          variant={props.variant === undefined ? 'outlined' : props.variant}
          label={props.label}
          fullWidth
          name={`${props.type}-${props.attr}`}
          value={Boolean(props.object[props.attr]) ? 'true' : 'false'}
          onChange={handleChange}
          required={props.required == undefined ? false : props.required}
          select
          size={props.size !== undefined ? props.size : 'small'}
          disabled={props.disabled === undefined ? false : props.disabled}
          onBlur={props.onBlur ? props.onBlur : undefined}
        >
          <MenuItem value='true'>Ja</MenuItem>
          <MenuItem value='false'>Nein</MenuItem>
        </TextField>
      </>
    );
  } else if (props.type === 'number' || props.type === 'euro') {
    return (
      <>
        <NumericFormat
          name={`${props.attr}`}
          suffix={props.type === 'euro' ? ' €' : undefined}
          value={getValue()}
          fixedDecimalScale
          decimalScale={props.type === 'euro' ? 2 : undefined}
          thousandsGroupStyle='thousand'
          thousandSeparator='.'
          decimalSeparator=','
          fullWidth
          customInput={TextField}
          onValueChange={handleChangeNumber}
          allowNegative={false}
          label={props.label}
          size={props.size !== undefined ? props.size : 'small'}
          disabled={props.disabled === undefined ? false : props.disabled}
        />
      </>
    );
  } else if (props.type === 'color') {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [showMoreColors, setShowMoreColors] = React.useState<boolean>(false);

    const normalizeHex = (v: string): string => {
      const s = (v ?? '').trim();
      if (s === '') return '';
      return s.startsWith('#') ? s : `#${s}`;
    };

    const isValidHex = (v: string): boolean =>
      /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(v);

    const currentFromProps = normalizeHex(getValue() ?? '#000000');
    const safeCurrent = isValidHex(currentFromProps)
      ? currentFromProps
      : '#000000';

    const [hexDraft, setHexDraft] = React.useState<string>(safeCurrent);

    React.useEffect(() => {
      setHexDraft(safeCurrent);
    }, [safeCurrent]);

    const setHexValue = (hex: string) => {
      const changeObject = { ...props.object };
      changeObject[props.attr] = hex;
      props.setObject(changeObject);
    };

    const commitIfValid = (v: string) => {
      const n = normalizeHex(v);
      if (isValidHex(n)) setHexValue(n);
    };

    const openPopover = (el: HTMLElement) => {
      setAnchorEl(el);
    };

    const closePopover = () => {
      setAnchorEl(null);
      setShowMoreColors(false);
    };

    const presets = [
      '#FF0000',
      '#FF7F00',
      '#FFFF00',
      '#00FF00',
      '#00FFFF',
      '#0000FF',
      '#8B00FF',
      '#FF00FF',
      '#A0522D',
      '#2B2B2B',
    ];

    const morePresets = [
      '#FF4D4D',
      '#FF9999',
      '#FFB84D',
      '#FFD699',
      '#FFF44D',
      '#FFF799',
      '#66FF66',
      '#99FF99',
      '#4DFFB8',
      '#99FFE6',
      '#4DD2FF',
      '#99E6FF',
      '#4D79FF',
      '#99B3FF',
      '#7A4DFF',
      '#B399FF',
      '#FF4DFF',
      '#FF99FF',
      '#D94DFF',
      '#E699FF',
    ];

    const ColorSwatch: React.FC<{
      color: string;
      selected: boolean;
      onPick: () => void;
    }> = ({ color, selected, onPick }) => (
      <Box
        role='button'
        tabIndex={0}
        onClick={onPick}
        onKeyDown={ev => {
          if (ev.key === 'Enter' || ev.key === ' ') onPick();
        }}
        sx={{
          width: 44,
          height: 28,
          borderRadius: 1,
          border: selected
            ? '2px solid rgba(0,0,0,0.8)'
            : '1px solid rgba(0,0,0,0.25)',
          backgroundColor: color,
          cursor: 'pointer',
        }}
      />
    );

    return (
      <>
        <TextField
          variant={props.variant ?? 'outlined'}
          label={props.label}
          fullWidth
          name={`color-${props.attr}`}
          value={hexDraft}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setHexDraft(e.target.value);
          }}
          onBlur={() => {
            if (isValidHex(normalizeHex(hexDraft))) {
              commitIfValid(hexDraft);
            } else {
              setHexDraft(safeCurrent);
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              if (isValidHex(normalizeHex(hexDraft))) {
                commitIfValid(hexDraft);
                closePopover();
              } else {
                setHexDraft(safeCurrent);
              }
            }
          }}
          required={props.required ?? false}
          size={props.size ?? 'small'}
          disabled={props.disabled ?? false}
          sx={{
            minWidth: 150,
            maxWidth: props.maxWidth ?? '100%',
          }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ maxLength: 7 }}
          error={isTooLong}
          helperText={helperText}
          FormHelperTextProps={{
            sx: {
              textAlign: 'right',
              color: isTooLong ? 'error.main' : 'text.secondary',
              m: 0,
              mt: 0.5,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <Stack direction='row' spacing={0.5} alignItems='center'>
                  <Tooltip title='Farbe auswählen'>
                    <IconButton
                      size='small'
                      onClick={e => openPopover(e.currentTarget)}
                      disabled={props.disabled ?? false}
                      aria-label='Farbe auswählen'
                    >
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: 0.75,
                          border: '1px solid rgba(0,0,0,0.25)',
                          backgroundColor: safeCurrent,
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </InputAdornment>
            ),
          }}
        />

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={closePopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ p: 2, width: 280 }}>
            <Stack spacing={1.5}>
              <TextField
                label='Hex'
                size='small'
                value={hexDraft}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setHexDraft(e.target.value);
                }}
                onBlur={() => {
                  if (isValidHex(normalizeHex(hexDraft))) {
                    commitIfValid(hexDraft);
                  } else {
                    setHexDraft(safeCurrent);
                  }
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    if (isValidHex(normalizeHex(hexDraft))) {
                      commitIfValid(hexDraft);
                      closePopover();
                    } else {
                      setHexDraft(safeCurrent);
                    }
                  }
                }}
                InputLabelProps={{ shrink: true }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ fontSize: 12, opacity: 0.7 }}>Picker</Box>

                <Box
                  sx={{
                    ml: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Tooltip title='Picker öffnen'>
                    <IconButton
                      size='small'
                      onClick={() => {
                        const input = document.getElementById(
                          `native-color-${props.attr}`
                        ) as HTMLInputElement | null;
                        input?.click();
                      }}
                      aria-label='Picker öffnen'
                      disabled={props.disabled ?? false}
                    >
                      <EditOutlinedIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>

                  <input
                    id={`native-color-${props.attr}`}
                    type='color'
                    value={safeCurrent}
                    onChange={e => {
                      setHexValue(e.target.value);
                      setHexDraft(e.target.value);
                    }}
                    style={{
                      width: 44,
                      height: 32,
                      padding: 0,
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: 1,
                }}
              >
                {presets.map(c => (
                  <ColorSwatch
                    key={c}
                    color={c}
                    selected={c.toLowerCase() === safeCurrent.toLowerCase()}
                    onPick={() => {
                      setHexValue(c);
                      setHexDraft(c);
                    }}
                  />
                ))}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  role='button'
                  tabIndex={0}
                  onClick={() => setShowMoreColors(v => !v)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setShowMoreColors(v => !v);
                    }
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <Typography variant='caption' sx={{ opacity: 0.7 }}>
                    Weitere Farben
                  </Typography>
                  {showMoreColors ? (
                    <KeyboardArrowUpIcon fontSize='small' />
                  ) : (
                    <KeyboardArrowDownIcon fontSize='small' />
                  )}
                </Box>
              </Box>

              <Collapse in={showMoreColors} timeout='auto' unmountOnExit>
                <Box
                  sx={{
                    mt: 0.5,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: 1,
                  }}
                >
                  {morePresets.map(c => (
                    <ColorSwatch
                      key={c}
                      color={c}
                      selected={c.toLowerCase() === safeCurrent.toLowerCase()}
                      onPick={() => {
                        setHexValue(c);
                        setHexDraft(c);
                      }}
                    />
                  ))}
                </Box>
              </Collapse>
            </Stack>
          </Box>
        </Popover>
      </>
    );
  } else {
    return (
      <>
        <TextField
          type={props.type === 'string' ? 'text' : props.type}
          variant={props.variant === undefined ? 'outlined' : props.variant}
          label={props.label}
          fullWidth
          name={`${props.type}-${props.attr}`}
          value={getValue()}
          onChange={handleChange}
          required={props.required == undefined ? false : props.required}
          multiline={props.rows === undefined ? false : true}
          rows={props.rows}
          size={props.size !== undefined ? props.size : 'small'}
          InputLabelProps={props.type === 'date' ? { shrink: true } : undefined}
          disabled={props.disabled === undefined ? false : props.disabled}
          onBlur={props.onBlur ? props.onBlur : undefined}
          error={Boolean(props.error) || isTooLong}
          helperText={props.helperText ?? helperText}
          FormHelperTextProps={{
            sx: {
              textAlign: 'right',
              color: isTooLong ? 'error.main' : 'text.secondary',
              m: 0,
              mt: 0.5,
            },
          }}
          inputProps={{
            maxLength: resolvedMaxLength,
          }}
        />
      </>
    );
  }
};
